import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import { get } from 'lodash';
import moment from 'moment';
import fastMemoize from 'fast-memoize';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList } from 'services/capabilityGroups/duck';
import DebounceInputChange from 'hoc/DebounceInputChange';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);

const generateFilterValue = (filter, row) => row[filter.id];
const memoizeFilterValue = fastMemoize(generateFilterValue);

class CapabilityGroupsTableContainer extends Component {
    constructor(props) {
        super(props);

        this.activeSearch = false;
        this.state = {
            showPagination: false,

            currentUrl: props.parsedUrl.href,
            list: null,

            // controlled table
            // filtered: [],
            // sorted: [],
            // page: 0,
            // pageSize: 25
            ...CapabilityGroupsTableContainer.convertUriToState(props.parsedUrl)
        };

        this.capabilityGroupTableInstance = React.createRef();
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }

    getColumns() {
        return [
            {
                Header: '',
                Cell: props => (
                    <React.Fragment>
                        {this.linkToEditCapabilityGroup(null, props.original)}
                    </React.Fragment>
                ),
                maxWidth: 30,
                resizable: false,
                sortable: false
            }, {
                Header: 'NAME',
                Cell: props => this.nameLink(null, props.original),
                accessor: 'name',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control capabilityGroupName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Group Name"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'CAPABILITIES',
                accessor: 'capabilityCount',
                maxWidth: 150,
                Cell: row => <CellDiv value={row.value} />,
            }, {
                Header: 'USERS',
                accessor: 'userCount',
                maxWidth: 150,
                Cell: row => <CellDiv value={row.value} />,
            }, {
                Header: 'CREATED',
                accessor: 'createdAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'right' }} />,
                maxWidth: 150
            }
        ];
    }

    componentDidMount() {
        this.props.fetchList();
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'capabilityGroups.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > DEFAULT_PAGE_SIZE) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...CapabilityGroupsTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    getCapabilityGroupTableInstance() {
        if (!this.capabilityGroupTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.capabilityGroupTableInstance;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getCapabilityGroupTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    dateFormatter(cell) {
        return `${moment(cell).format('MM-DD-YYYY')}`;
    }

    linkToEditCapabilityGroup(cell, row) {
        return (
            <Link key={0} title="Edit Capability Group" to={`/admin/capability-groups/${row.id}/edit`}>
                <span className="icon-edit" />
            </Link>
        );
    }

    nameLink(cell, row) {
        return (
            <Link title="View Capability Group" to={`/admin/capability-groups/${row.id}`}>
                {row.name}
            </Link>
        );
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'name':
                if (filter.value) {
                    query.capabilityGroupName = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.capabilityGroupName) {
            filtered.push({ id: 'name', value: query.capabilityGroupName });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir === 'desc' });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const data = this.props.capabilityGroups.list;
        const dataFetched = this.props.capabilityGroups.listFetched;

        return (
            <Grid fluid>
                <ButtonStandardPrimary
                    type="submit"
                    onClick={() => this.props.push('/admin/capability-groups/create')}>
                        Create Capability Group
                </ButtonStandardPrimary>
                <br />
                <br />
                {dataFetched ? (
                    <StyledReactTable
                        data={data}
                        ref={this.capabilityGroupTableInstance}
                        defaultPageSize={25}
                        minRows={10}
                        filtered={this.state.filtered}
                        sorted={this.state.sorted}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        onFilteredChange={(filtered, column, value) => {
                            this.onFilteredChangeCustom(value, column.id || column.accessor);
                        }}
                        onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                        onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                        onPageSizeChange={(pageSize, page) => this.setState({
                            page, pageSize
                        }, () => this.mirrorStateToUri())}
                        getTheadThProps={() => ({
                            style: {
                                backgroundColor: '#E2E2E2',
                            }
                        })}
                        getTheadFilterThProps={() => ({
                            style: {
                                overflow: 'inherit' // allows react-select to work
                            }
                        })}
                        columns={this.getColumns()}
                        defaultSorted={[{
                            id: 'name', desc: false
                        }]}
                        className="-highlight"
                        showPagination={this.state.showPagination}
                        PaginationComponent={Pagination}
                    />
                ) : (
                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    capabilityGroups: state.capabilityGroups,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, push })(CapabilityGroupsTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/CapabilityGroupsTableContainer.js